<template>
    <Card>
        <template #title>
            <div v-if="!editMode" class="p-d-flex">
                <div class="p-col">{{ qualification.name }}</div>
                <div v-if="allowEdit" class="p-d-flex p-jc-between">
                    <Button icon="pi pi-pencil" class="p-button-success p-button-rounded p-mr-1"
                        @click="toggleEditMode" :disabled="disabled" />
                    <Button icon="pi pi-trash" class="p-button-danger p-button-rounded"
                        @click="displayDeleteConfirmation = true" :disabled="disabled" />
                    <YesNoDialog
                        :displayModal.sync="displayDeleteConfirmation"
                        @on-yes="deleteQualification"/>
                </div>
            </div>
        </template>
        <template #content>
            <div v-if="editMode" class="p-d-flex p-jc-between p-flex-wrap">
                <div>
                    <span>
                        <label for="title">{{ $t('title') }}</label>
                        <InputText id="title" type="text" v-model="modifiedQualification.name" />
                    </span>
                </div>
                <div class="p-d-flex p-jc-between p-ai-center" style="width: 4.5rem">
                    <Button icon="pi pi-check" class="p-button-success p-button-rounded p-mr-1"
                        @click="saveQualification"/>
                    <Button icon="pi pi-times" class="p-button-danger p-button-rounded"
                        @click="cancelEditMode" />
                </div>
            </div>
            <div v-if="editMode">
                <span>
                    <label for="description">{{ $t('description') }}</label>
                    <Textarea id="description" v-model="modifiedQualification.description" :autoResize="true" />
                </span>
            </div>
            <div v-else style="font-style: italic;">
                <span style="white-space: pre-wrap">{{ qualification.description }}</span>
            </div>
            <div v-if="editMode && allowUsersSelection">
                {{ $t('users') }}
                <MultiSelect 
                    v-model="modifiedQualification.userIds" 
                    :options="associates"
                    optionValue="id"
                    optionLabel="name" 
                    filter 
                    :placeholder="$t('selectUsers')"
                />
            </div>
            <div v-if="!editMode && qualification.file && qualification.file.name" class="p-mt-4">
                <span class="pi pi-file"></span>
                <a href="#" @click="downloadFile()">{{ qualification.file.name }}</a>
            </div>
            <div v-else-if="editMode" class="p-mt-4">
                <Button ref="uploadLabel" icon="pi pi-file" class="p-button-primary p-button-rounded" :label="fileUploadLabel" @click="$refs.upload.click()"></Button>
                <input ref="upload" type="file" @change="onFileSelected" name="filename" style="display: none">
                <!-- <FileUpload mode="basic" :customUpload="true" :chooseLabel="fileUploadLabel" @uploader="deferredUpload"></FileUpload> -->
            </div>
        </template>
        <template #footer>
            <div class="p-d-flex p-jc-between p-flex-wrap">
                <div>
                    {{ $t('acquiredAt') }}
                    <Calendar v-if="editMode" v-model="completionDate" dateFormat="dd.mm.yy" :showIcon="true" />
                    <span v-else>
                        {{ completionDate }}
                    </span>
                </div>
                <div>
                    {{ $t('duration') }}
                    <Dropdown v-if="editMode" v-model="modifiedQualification.duration" 
                        :options="durations" 
                        :filter="true"
                        optionLabel="name" 
                        optionValue="name"
                        :placeholder="$t('chooseDuration')" />
                    <span v-else>
                        {{ qualification.duration }}
                    </span>
                </div>
            </div>
        </template>
    </Card>
</template>

<script>
import _ from 'lodash';
import moment from 'moment/moment';
import YesNoDialog from '@/components/Shared/YesNoDialog.vue';

export default {
    props: {
        disabled: Boolean,
        allowEdit: Boolean,
        qualification: {
            id: Number,
            name: String,
            duration: String,
            description: String,
            completionDate: String,
            file: {
                type: Object,
                default: {}
            },
            edit: {
                type: Boolean,
                default: false
            }
        },
        associates: {
            type: Array,
            default: () => []
        },
        userId: String
    },
    emits: ["save", "delete", "download", "cancel-edit"],
    data() {

        return {
            editMode: this.qualification.edit,
            displayDeleteConfirmation: false,
            modifiedQualification: { ...this.qualification, userIds: [Number(this.userId)] },
            completionDate: null,
            shouldUpdateComputedPropertyHack: 0,
            allowUsersSelection: Boolean(this.associates.length),
            durations: [
                { name: "< 1 dag" },
                { name: "1 dag" },
                { name: "2 dage" },
                { name: "3 dage" },
                { name: "4 dage" },
                { name: "5 dage" },
                { name: "6 dage" },
                { name: "7 dage" },
                { name: "8 dage" },
                { name: "9 dage" },
                { name: "10 dage" },
                { name: "11 dage" },
                { name: "12 dage" },
                { name: "13 dage" },
                { name: "1 uge" },
                { name: "2 uger" },
                { name: "3 uger" },
                { name: "1 måned" },
                { name: "2 måneder" },
                { name: "3 måneder" },
                { name: "4 måneder" },
                { name: "5 måneder" },
                { name: "6 måneder" },
                { name: "> 6 måneder" }
            ]
        };
    },
    computed: {
        fileUploadLabel() {
            this.shouldUpdateComputedPropertyHack;
            return this.modifiedQualification.file.name || 'Upload File'
        }
    },
    mounted() {
        this.completionDate = moment(this.modifiedQualification.completionDate).format("DD.MM.YYYY");
        this.modifiedQualification.file = this.modifiedQualification.file || {};
    },
    watch: {
        completionDate(value) {
            this.modifiedQualification.completionDate = moment(value, "DD.MM.YYYY").toDate();
        },
        qualification: {
          handler: function(updatedQualification) {
            Object.assign(this.modifiedQualification, updatedQualification);
          },
          immediate: true
        }
    },
    methods: {
        downloadFile() {
            this.$emit("download", this.qualification);
        },
        onFileSelected(event) {
            this.$set(this.modifiedQualification, 'file', event.target.files[0])
            this.shouldUpdateComputedPropertyHack++;
        },
        toggleEditMode() {
            this.editMode = !this.editMode;
        },
        cancelEditMode() {
            this.$emit("cancel-edit", this.modifiedQualification);
            this.toggleEditMode();
        },
        saveQualification() {
            if (!_.isEqual(this.qualification, this.modifiedQualification)) {
                this.$emit("save", this.modifiedQualification);
            }
            this.toggleEditMode();
        },
        deleteQualification() {
            this.$emit("delete", this.modifiedQualification);
        }
    },
    components: { YesNoDialog }
}
</script>

<i18n lang="yaml" locale="en">
    acquiredAt: "Acquired at"
    duration: "Duration"
    chooseDuration: "Choose duration"
    users: "Users"
    selectUsers: "Select users"
</i18n>

<i18n lang="yaml" locale="dk">
    acquiredAt: "Gennemført d."
    duration: "Varighed"
    chooseDuration: "Vælg varighed"
    users: "Brugere"
    selectUsers: "Vælg brugere"
</i18n>